// =============================================================================
// ui/tabs/ComparePlans.tsx — "Choose your plan". #65.
// -----------------------------------------------------------------------------
// Relocated from the old client's `openUpgradeModal` (app/index.html:49134).
// Reached from the "Compare plans →" link on the plan card, which is its only
// entry point there and here.
//
// ── ⚠ PRICES ARE READ, NEVER RETYPED ──
//
// The old client's note: the mockup carried its own copy of the prices, "which
// would drift the moment a price changed". Every figure here comes from
// `BILLING_PLANS`, and a test asserts the modal shows no price that object does
// not have.
//
// ⚠ THE FREE COLUMN'S "$0 /forever" IS THE ONE HARDCODED PRICE, in the old
// client too. It is not a plan and there is nothing to read it from.
//
// ── ⚠ IT MUST STAY CLOSEABLE ──
//
// The old client keeps width and header-suppression as SEPARATE classes because
// combining them once made this exact modal silently lose its title and its ×.
// This one has a header × and a footer Close, and a test counts both.
// =============================================================================

import { useState } from 'react';
import { BILLING_PLANS, type BillingPlan } from '../../selectors/billing';
import { FREE_ACCOUNT_LIMIT } from '../../selectors/freeTier';
import { tierFeatureRows, type TierRow } from '../../selectors/tierFeatures';
import type { BillingInterval } from '../../io/billing/checkout';

export interface ComparePlansProps {
  onClose: () => void;
  /** Start a purchase. `founder` false is the "or pay standard Pro" link. */
  onBuy: (args: { interval: BillingInterval; founder: boolean }) => void;
  busy?: boolean;
}

/** `$180/yr` → `['$180', 'yr']`. */
function split(plan: BillingPlan): [string, string] {
  const [amount, unit] = String(plan.price).split('/');
  return [amount ?? '', unit ?? ''];
}

function Cell({ v }: { v: boolean | string }) {
  if (v === true) return <span className="c up-yes" aria-label="included">✓</span>;
  if (v === false) return <span className="c up-no" aria-label="not included">—</span>;
  // ⚠ "Unlimited" reads as a win, a bare number as a limit — the old client
  // colours them on exactly that test.
  return <span className={`c ${/unlimited/i.test(v) ? 'up-yes' : 'up-no'}`}>{v}</span>;
}

function Row({ r }: { r: TierRow }) {
  return (
    <div className="up-row">
      <span>{r.lead ? <b>{r.lead}</b> : null}{r.label}</span>
      <Cell v={r.free} />
      <Cell v={r.pro} />
    </div>
  );
}

export function ComparePlans({ onClose, onBuy, busy }: ComparePlansProps) {
  const [interval, setInterval] = useState<BillingInterval>('annual');
  const annual = interval === 'annual';
  const pro = annual ? BILLING_PLANS.pro_annual : BILLING_PLANS.pro_monthly;
  const founder = annual ? BILLING_PLANS.founder_annual : BILLING_PLANS.founder_monthly;
  const [proAmount, proUnit] = split(pro);
  const [fAmount, fUnit] = split(founder);

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Choose your plan">
      <div className="modal xwide" data-testid="compare-plans">
        <header className="modal-head">
          <b>Choose your plan</b>
          <button type="button" className="btn" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="modal-body">
          <div className="up-bill">
            <div className="up-bill-in">
              <button
                type="button" className={annual ? 'on' : ''}
                onClick={() => setInterval('annual')}
              >
                Annual <span className="up-save">2 MO FREE</span>
              </button>
              <button
                type="button" className={annual ? '' : 'on'}
                onClick={() => setInterval('monthly')}
              >
                Monthly
              </button>
            </div>
          </div>

          <div className="up-cols">
            <div className="up-col">
              <div className="up-tag">Free</div>
              {/* ⚠ The one hardcoded price — see the header. */}
              <div className="up-price">$0<small> /forever</small></div>
              <div className="up-alt">
                {FREE_ACCOUNT_LIMIT} trading account{FREE_ACCOUNT_LIMIT === 1 ? '' : 's'}
              </div>
              <div className="up-current">Your current plan</div>
            </div>

            <div className="up-col paid">
              <div className="up-tag">Pro</div>
              <div className="up-price">{proAmount}<small> /{proUnit}</small></div>
              <div className="up-alt">
                {annual ? `${pro.sub} · unlimited accounts` : 'billed monthly · unlimited accounts'}
              </div>
              <div className="up-founder">
                <div className="f-h">Founder rate <span className="up-seats">FIRST 100</span></div>
                <div className="f-p">{fAmount} <span className="f-u">/{fUnit}</span></div>
                <div className="f-n">
                  Same Pro features. Your rate is held for as long as you stay
                  subscribed — while seats last.
                </div>
              </div>
              <button
                type="button" className="btn primary up-cta" disabled={busy}
                onClick={() => onBuy({ interval, founder: true })}
              >
                Get Founder — {founder.price}
              </button>
              <div className="up-std-wrap">
                <button
                  type="button" className="up-std" disabled={busy}
                  onClick={() => onBuy({ interval, founder: false })}
                >
                  or pay standard Pro
                </button>
              </div>
            </div>
          </div>

          <div className="up-feat-h">What you get</div>
          <div>
            <div className="up-row head"><span>Feature</span><span>Free</span><span>Pro</span></div>
            {tierFeatureRows().map((r) => <Row key={(r.lead ?? '') + r.label} r={r} />)}
          </div>

          <p className="up-fine">
            7-day money-back guarantee · cancel anytime · prices in USD<br />
            Your trades stay yours — export your data on any plan.
          </p>
        </div>

        <div className="modal-foot">
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
